import React, { useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { login } from "../services/auth.ts";

// Only a path on this app is a valid redirect target - a "from" is attacker-controllable via the
// URL (it arrives as a query param, unlike the React Router state set by ProtectedRoute), so an
// absolute/protocol-relative value must be rejected rather than handed to navigate().
const isSafeRedirect = (path: string | null | undefined): path is string =>
  !!path && path.startsWith("/") && !path.startsWith("//");

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const stateFrom = (location.state as { from?: string } | null)?.from;
  const queryFrom = searchParams.get("from");
  const redirectTo = [stateFrom, queryFrom].find(isSafeRedirect) || "/questions";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const response = await login(email, password);
      const token = response.data.access_token;
      if (token) {
        localStorage.setItem("email", email);
        localStorage.setItem("token", token);
        localStorage.setItem("user_id", String(response.data.user_id));
        setSuccess("Login successful!");
        setEmail("");
        setPassword("");
        navigate(redirectTo);
      } else {
        setError("No token received from server");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center px-6 py-16">
      <div className="bg-white rounded-lg border border-border p-8 w-full max-w-md flex flex-col items-center">
        <h2 className="text-5xl font-bold mb-6 text-ink font-display">Login</h2>
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
          <div className="flex flex-col">
            <label className="text-xl font-semibold mb-1 text-gray-500 font-body">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="rounded-lg px-4 py-3 text-xl font-body border border-border focus:border-accent focus:outline-none bg-white text-ink"
              placeholder="you@email.com"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xl font-semibold mb-1 text-gray-500 font-body">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="rounded-lg px-4 py-3 pr-12 text-xl font-body border border-border focus:border-accent focus:outline-none bg-white text-ink w-full"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                onMouseDown={e => e.preventDefault()}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-ink"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.58 10.58a2 2 0 002.83 2.83M9.88 5.09A9.77 9.77 0 0112 5c5 0 9 4.5 9 7 0 1.06-.62 2.4-1.68 3.68M6.1 6.1C4.24 7.44 3 9.29 3 12c0 2.5 4 7 9 7 1.3 0 2.53-.27 3.63-.75" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-primary text-white text-xl font-semibold font-body py-3 px-8 rounded-lg hover:bg-ink transition-colors duration-200 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="text-lg font-semibold font-body text-gray-500 hover:text-ink transition-colors duration-200"
          >
            Forgot password?
          </button>
        </form>
        {error && <div className="mt-4 text-xl text-incorrect font-semibold font-body">{error}</div>}
        {success && <div className="mt-4 text-xl text-success font-semibold font-body">{success}</div>}
      </div>
    </div>
  );
};

export default Login;
